// imports
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Badge,
  Box,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { FC, Reducer, SyntheticEvent, useCallback, useContext, useReducer, useRef } from 'react';

// components
import AddCardForm from './AddCardForm';
import ExistingCards from './ExistingCards';
import AddBankAccountForm from 'components/main/Settings/PaymentMethods/AddBankAccountForm';

// interfaces, constants, helper
import { LoadingButton } from 'components/common/LoadingButton';
import {
  PROCESSING_TEXT,
  SUBSCRIBE_TEXT,
  SUBSCRIPTION_TEXT,
  SUMMARY_TEXT,
  TOTAL_BILLED_TEXT,
  USE_EXISTING_PAYMENT_METHOD_TEXT,
  ADD_NEW_PAYMENT_METHOD_TEXT,
  YOU_WILL_BE_CHARGED_AFTER_7_DAYS_TEXT,
  PAYMENT_TYPE_ACH_LABEL,
  PAYMENT_TYPE_CARD_LABEL,
  CLICK_SUBSCRIBE_TO_START_YOUR_TRIAL_TEXT,
} from 'constants/constant';
import { SECONDARY_FONT } from 'constants/fonts';
import { CheckoutTabs } from 'constants/tabs';
import { StripePaymentMethod, StripePaymentMethodType } from 'generated/graphql';
import { CheckoutFormType, Nullable } from 'interfaces';
import { VerticalDottedBorder } from 'styles/commonComponentStyle';
import { UserSubscriptionsContext } from 'contexts/UserSubscriptions';
import { formatTimeStamp, getBillingInfo } from 'lib/helper';
import { Action, ActionType, State, checkoutFormReducer, initialState } from 'reducer/checkoutFormReducer';

const CheckoutForm: FC<CheckoutFormType> = ({
  priceId,
  amount,
  interval,
  staff,
  isTrialEnded,
  mode,
  onComplete,
  billAbleAmount,
}) => {
  // refs
  const stripeFormRef = useRef<Nullable<HTMLFormElement>>(null);
  const existingCardsRef = useRef<Nullable<HTMLFormElement>>(null);

  const [state, dispatch] = useReducer<Reducer<State, Action>>(checkoutFormReducer, initialState);
  const { formLoading, addCardLoading, expanded, loadStripeForm, selectedCard, paymentType } = state;
  const intervalText = `${interval}LY`;

  const setFormLoading = useCallback(
    (formLoading: boolean) => {
      dispatch({
        type: ActionType.SET_FORM_LOADING,
        formLoading,
      });
    },
    [dispatch],
  );

  const setAddCardLoading = useCallback(
    (addCardLoading: boolean) => {
      dispatch({
        type: ActionType.SET_ADD_CARD_LOADING,
        addCardLoading,
      });
    },
    [dispatch],
  );

  const setSelectedCard = useCallback(
    (selectedCard: Nullable<StripePaymentMethod>) => {
      dispatch({
        type: ActionType.SET_SELECTED_CARD,
        selectedCard,
      });
    },
    [dispatch],
  );

  // contexts
  const { userSubscription } = useContext(UserSubscriptionsContext);
  const { trialEnd } = userSubscription || {};

  // Disable Subscribe when payment is required but not provided:
  // - Has subscription (upgrade): must use existing card
  // - Trial ended (re-subscribe): must have payment (existing card or add new)
  // - First-time: allow trial without payment
  const hasExistingSubscription = Boolean(userSubscription?.id);
  const needsPaymentMethod = hasExistingSubscription || isTrialEnded;
  const isDisabled = needsPaymentMethod
    ? expanded === CheckoutTabs.EXISTING
      ? !selectedCard
      : hasExistingSubscription
    : false;
  const endDate = formatTimeStamp(trialEnd ?? '');

  const handleChange = (panel: CheckoutTabs) => (_: SyntheticEvent, newExpanded: boolean) => {
    dispatch({
      type: ActionType.SET_EXPANDED,
      expanded: newExpanded ? panel : CheckoutTabs.NEW,
    });
    stripeFormRef.current?.reset();
    dispatch({ type: ActionType.SET_SELECTED_CARD, selectedCard: null });
    dispatch({ type: ActionType.SET_FORM_LOADING, formLoading: false });
  };

  const openAddCardForm = () => {
    dispatch({ type: ActionType.SET_EXPANDED, expanded: CheckoutTabs.NEW });
    dispatch({ type: ActionType.SET_LOAD_STRIPE_FORM, loadStripeForm: true });
  };

  const handleSubmit = () => {
    if (expanded === CheckoutTabs.NEW) {
      stripeFormRef.current?.submit();
    } else {
      existingCardsRef.current?.submit();
    }
  };

  const handleRefetch = () => {
    existingCardsRef.current?.refetch();
    dispatch({ type: ActionType.SET_EXPANDED, expanded: CheckoutTabs.EXISTING });
  };

  return (
    <Grid container spacing={4} mb={4}>
      <Grid item xs={12} md={8}>
        <Accordion
          expanded={expanded === CheckoutTabs.EXISTING}
          onChange={handleChange(CheckoutTabs.EXISTING)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
            <Typography component="span">
              <Checkbox checked={expanded === CheckoutTabs.EXISTING} />
              {USE_EXISTING_PAYMENT_METHOD_TEXT}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExistingCards
              setSelectedCard={setSelectedCard}
              selectedCard={selectedCard}
              setFormLoading={setFormLoading}
              priceId={priceId}
              ref={existingCardsRef}
              openAddCardForm={openAddCardForm}
              staff={staff}
              mode={mode}
              onComplete={onComplete}
              isTrialEnded={isTrialEnded}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === CheckoutTabs.NEW}
          onChange={() => {
            dispatch({ type: ActionType.SET_LOAD_STRIPE_FORM, loadStripeForm: true });
          }}>
          <AccordionSummary
            onClick={() => dispatch({ type: ActionType.SET_EXPANDED, expanded: CheckoutTabs.NEW })}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2-content"
            id="panel2-header">
            <Typography component="span">
              <Checkbox checked={expanded === CheckoutTabs.NEW} />
              {ADD_NEW_PAYMENT_METHOD_TEXT}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loadStripeForm && expanded === CheckoutTabs.NEW && (
              <>
                <RadioGroup
                  row
                  value={paymentType}
                  onChange={(_, value) =>
                    dispatch({
                      type: ActionType.SET_PAYMENT_TYPE,
                      paymentType: value as StripePaymentMethodType,
                    })
                  }
                  sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={StripePaymentMethodType.Card}
                    control={<Radio />}
                    label={PAYMENT_TYPE_CARD_LABEL}
                  />
                  <FormControlLabel
                    value={StripePaymentMethodType.Ach}
                    control={<Radio />}
                    label={PAYMENT_TYPE_ACH_LABEL}
                  />
                </RadioGroup>
                {paymentType === StripePaymentMethodType.Card ? (
                  <AddCardForm
                    setFormLoading={setAddCardLoading}
                    formLoading={addCardLoading}
                    onComplete={handleRefetch}
                  />
                ) : (
                  <AddBankAccountForm
                    setFormLoading={setAddCardLoading}
                    formLoading={addCardLoading}
                    onComplete={handleRefetch}
                  />
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={{ padding: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" fontFamily={SECONDARY_FONT} mb={5} color="white">
              {SUMMARY_TEXT}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">{SUBSCRIPTION_TEXT}</Typography>
              <Badge color="success" badgeContent={intervalText} />
            </Box>
            <Box display="flex" justifyContent="center" gap={1} mt={2}>
              <Typography variant="subtitle1" component="p">
                $
              </Typography>
              <Typography variant="h3" fontFamily={SECONDARY_FONT}>
                {amount}
              </Typography>
              <Typography variant="subtitle1" component="p" mt={1}>
                /Seat
              </Typography>
            </Box>
            <VerticalDottedBorder mt={2}>
              <Typography variant="subtitle1" fontFamily={SECONDARY_FONT} color="white">
                {TOTAL_BILLED_TEXT}
              </Typography>
              <Typography variant="subtitle1" fontFamily={SECONDARY_FONT} color="white">
                ${billAbleAmount}
              </Typography>
            </VerticalDottedBorder>
            {!isTrialEnded && (
              <Box mt={2}>
                <Typography variant="caption" color="grey">
                  {trialEnd
                    ? getBillingInfo(endDate, `${amount}`, intervalText)
                    : YOU_WILL_BE_CHARGED_AFTER_7_DAYS_TEXT}
                </Typography>
              </Box>
            )}
          </CardContent>
          <CardActions>
            <LoadingButton
              loading={formLoading}
              loadingText={PROCESSING_TEXT}
              onClick={handleSubmit}
              disabled={isDisabled}
              text={SUBSCRIBE_TEXT}
              size="large"
              fullWidth
              appLoader
            />
          </CardActions>
          {!(isTrialEnded && trialEnd) && (
            <Typography variant="caption" component="p" color="grey" mt={2}>
              {CLICK_SUBSCRIBE_TO_START_YOUR_TRIAL_TEXT}
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default CheckoutForm;
